import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {Alert, IconButton, Popper, Tooltip} from '@mui/material';
import copy from 'copy-to-clipboard';
import PropTypes from 'prop-types';
import React, {useEffect, useRef, useState} from 'react';

function Notification({
  customNotificationComponent,
  hasCopyError,
  onDismiss,
  title,
}) {
  function handleClick(e) {
    e.stopPropagation();
    onDismiss();
  }

  return (
    customNotificationComponent || (
      <Alert
        action={
          <IconButton
            aria-label="close notification"
            color="inherit"
            onClick={handleClick}
            size="small"
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        }
        severity={hasCopyError ? 'error' : 'success'}
      >
        {hasCopyError ? 'Copying error; please try again.' : `Copied ${title}!`}
      </Alert>
    )
  );
}

Notification.propTypes = {
  customNotificationComponent: PropTypes.elementType,
  hasCopyError: PropTypes.bool,
  onDismiss: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
};

export default function CopyButton({
  buttonStyleOverrides = null,
  customNotificationComponent = null,
  notificationLeaveDelay = 2500,
  notificationPlacement = 'top',
  onCopy = () => {},
  'data-testid': qe,
  showNotification = true,
  size = 'small',
  textToCopy,
  tooltipPlacement = 'bottom',
  title = 'text',
  isDisabled,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isCopyNotificationOpen, setIsCopyNotificationOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [hasCopyError, setHasCopyError] = useState(false);
  const timer = useRef();

  function handleCopyButtonClick(event) {
    const isTextCopied = copy(textToCopy);

    clearTimeout(timer.current);
    setAnchorEl(event.currentTarget);
    setIsCopyNotificationOpen(true);
    setIsTooltipOpen(false);
    setHasCopyError(!isTextCopied);
    onCopy(isTextCopied, event);

    timer.current = setTimeout(() => {
      setAnchorEl(null);
      setIsCopyNotificationOpen(false);
      setHasCopyError(false);
    }, notificationLeaveDelay);
  }

  function handleTooltipOpen() {
    setIsTooltipOpen(!isCopyNotificationOpen);
  }

  function handleTooltipClose() {
    setIsTooltipOpen(false);
  }

  function handleDismiss() {
    clearTimeout(timer.current);
    setIsCopyNotificationOpen(false);
  }

  useEffect(() => clearTimeout(timer.current), []);

  return (
    <>
      <Tooltip
        onClose={handleTooltipClose}
        onOpen={handleTooltipOpen}
        open={isTooltipOpen}
        placement={tooltipPlacement}
        title={`Tap button to copy ${title}`}
      >
        <IconButton
          data-testid={qe}
          onClick={handleCopyButtonClick}
          size={size}
          sx={{borderRadius: 1, padding: 0.5, ...buttonStyleOverrides}}
          disabled={isDisabled}
        >
          <ContentCopyIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      {showNotification && (
        <Popper
          anchorEl={anchorEl}
          disablePortal
          open={isCopyNotificationOpen}
          placement={notificationPlacement}
          sx={{zIndex: 'tooltip'}}
        >
          <Notification
            customNotificationComponent={customNotificationComponent}
            hasCopyError={hasCopyError}
            onDismiss={handleDismiss}
            title={title}
          />
        </Popper>
      )}
    </>
  );
}

CopyButton.propTypes = {
  buttonStyleOverrides: PropTypes.object,
  customNotificationComponent: PropTypes.elementType,
  notificationLeaveDelay: PropTypes.number,
  notificationPlacement: PropTypes.oneOf([
    'auto',
    'auto-end',
    'auto-start',
    'bottom',
    'bottom-end',
    'bottom-start',
    'left',
    'left-end',
    'left-start',
    'right',
    'right-end',
    'right-start',
    'top',
    'top-end',
    'top-start',
  ]),
  onCopy: PropTypes.func,
  'data-testid': PropTypes.string,
  showNotification: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  textToCopy: PropTypes.string.isRequired,
  title: PropTypes.string,
  tooltipPlacement: PropTypes.oneOf([
    'bottom',
    'bottom-end',
    'bottom-start',
    'left',
    'left-end',
    'left-start',
    'right',
    'right-end',
    'right-start',
    'top',
    'top-end',
    'top-start',
  ]),
  isDisabled: PropTypes.bool,
};
